import { Link } from "react-router-dom";
import { useSettings } from "@/lib/SettingsContext";
import usePageMeta from "@/lib/usePageMeta";
import renderRich from "@/lib/renderRich";
import { Star, ArrowRight, Sparkles, Quote, Gift, PartyPopper, Stamp, DoorOpen } from "lucide-react";

const Stars = ({ size = 14 }) => (
  <span className="inline-flex items-center gap-0.5 text-champagne">
    {[...Array(5)].map((_, i) => (
      <Star key={i} size={size} fill="currentColor" strokeWidth={0} />
    ))}
  </span>
);

export default function Home() {
  usePageMeta(
    "Beautify · Deborah Louise Beauty & Massage — Wadebridge, Cornwall",
    "Tailored massage, facials, lash & brow and nail treatments with Deborah Louise. 100% 5-star rated. Two calm studios in Wadebridge, Cornwall. Book online."
  );
  const { brand: BRAND, images: IMAGES, reviews: REVIEWS, locations: LOCATIONS, offers: OFFERS, event: EVENT, content: CONTENT } = useSettings();
  const HOME = (CONTENT && CONTENT.home) || {};

  const categories = [
    {
      title: "Massage & Holistic",
      desc: "Swedish, Hot Stones, Lomi Lomi, Indian Head, Warm Bamboo & Pregnancy.",
      image: IMAGES.hotStones,
      cta: "/treatments#massage",
      test: "cat-massage-link",
    },
    {
      title: "Facials & Skincare",
      desc: "Dr Hauschka luxury & express facials, tailored to your skin.",
      image: IMAGES.facial,
      cta: "/treatments#beauty",
      test: "cat-facials-link",
    },
    {
      title: "Nails, Lashes & Brows",
      desc: "Shellac gel, Biab overlays, lash lift & tint, brow shaping.",
      image: IMAGES.manicure,
      cta: "/treatments#beauty",
      test: "cat-nails-link",
    },
  ];

  const offersItems = (OFFERS && OFFERS.items) || [];
  const showOffers = OFFERS && OFFERS.active && offersItems.length > 0;

  // Featured event visibility: active flag + end_date (inclusive) must be today or future.
  const showEvent = (() => {
    if (!EVENT || !EVENT.active) return false;
    if (!EVENT.end_date) return true;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(`${EVENT.end_date}T23:59:59`);
    return !isNaN(end.getTime()) && end.getTime() >= today.getTime();
  })();

  return (
    <div data-testid="home-page" className="bg-grain">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 pt-8 sm:pt-12 pb-16 sm:pb-24 grid lg:grid-cols-12 gap-10 sm:gap-12 items-center">
          <div className="lg:col-span-7 stagger">
            <span className="inline-flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.3em] uppercase text-plum font-semibold">
              <Sparkles size={14} /> Wadebridge · Cornwall
            </span>
            <h1 className="mt-5 sm:mt-6 font-display text-[2.5rem] leading-[1.05] sm:text-5xl md:text-6xl lg:text-7xl lg:leading-[0.95] text-plum-deep">
              {HOME.heading_line1 || "Welcome"} <br />
              <em className="text-plum">{HOME.heading_line2 || "to Beautify."}</em>
            </h1>
            <div data-testid="hero-welcome" className="mt-6 sm:mt-8 max-w-xl">
              <p className="font-display italic text-xl sm:text-2xl text-plum">
                {HOME.lead}
              </p>
              <div className="mt-4 space-y-4 text-[15px] sm:text-base text-foreground/75 leading-relaxed">
                {(HOME.paragraphs || []).map((p, i) => (
                  <p key={i}>{renderRich(p)}</p>
                ))}
                <p className="font-display italic text-lg sm:text-xl text-plum-deep pt-1">
                  {HOME.signoff}{" "}
                  {HOME.signature && (
                    <span className="not-italic font-sans text-sm text-foreground/60 whitespace-nowrap">
                      {HOME.signature}
                    </span>
                  )}
                </p>
              </div>
            </div>

            <div className="mt-8 sm:mt-10 flex flex-wrap items-center gap-3 sm:gap-4">
              <Link
                to="/contact"
                data-testid="hero-book-now-btn"
                className="inline-flex items-center gap-2 rounded-full bg-plum text-white px-6 sm:px-7 py-3 sm:py-3.5 text-sm font-medium hover:bg-plum-light transition-all hover:-translate-y-0.5 shadow-md"
              >
                Book a treatment <ArrowRight size={16} />
              </Link>
              <Link
                to="/treatments"
                data-testid="hero-view-treatments-btn"
                className="inline-flex items-center gap-2 rounded-full border border-plum/30 text-plum px-6 sm:px-7 py-3 sm:py-3.5 text-sm font-medium hover:bg-plum/5 transition"
              >
                View treatments
              </Link>
            </div>

            <div className="mt-10 sm:mt-12 flex flex-wrap items-center gap-4 sm:gap-5">
              <div
                data-testid="walk-in-notice"
                className="inline-flex items-center gap-2 rounded-full border border-champagne/70 bg-champagne/10 text-plum px-4 py-2 text-xs sm:text-sm font-semibold"
              >
                <DoorOpen size={15} className="shrink-0" />
                Walk-in appointments available at Elixir Health, Molesworth Street
              </div>
            </div>

            <div className="mt-6 sm:mt-8 flex items-center gap-4 sm:gap-5">
              <Stars />
              <div className="text-xs sm:text-sm">
                <span className="font-semibold text-plum">100%</span>
                <span className="text-foreground/60"> · {BRAND.reviewsSummary.count} reviews on Facebook &amp; Google</span>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5 relative">
            <div className="absolute -top-6 -left-6 w-40 h-40 rounded-full bg-blush hidden md:block" />
            <div className="relative rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden shadow-xl border border-white">
              <img
                src={IMAGES.workspace}
                alt="The Beautify treatment room"
                fetchPriority="high"
                className="w-full h-[360px] sm:h-[440px] lg:h-[520px] object-cover"
                data-testid="hero-image"
              />
            </div>
            <div className="absolute -bottom-6 -right-2 bg-white/95 backdrop-blur px-6 py-4 rounded-2xl shadow-lg max-w-xs border border-blush-deep/30 hidden md:block">
              <div className="flex items-center gap-2 text-champagne mb-1">
                <Stars size={12} />
              </div>
              <p className="text-sm italic text-foreground/80 leading-snug">
                "Felt so relaxed afterwards. Very welcoming & informative."
              </p>
              <p className="text-xs text-foreground/55 mt-1">— Toni, Hot Stone Massage</p>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED EVENT — poster-style */}
      {showEvent && (
        <section
          id="event"
          data-testid="event-section"
          className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 pt-6 sm:pt-10 scroll-mt-32"
        >
          <div className="relative overflow-hidden rounded-[1.5rem] sm:rounded-[2rem] border-2 border-champagne/70 bg-gradient-to-br from-blush/50 via-white to-blush/30 shadow-xl">
            <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full bg-plum/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -left-16 w-72 h-72 rounded-full bg-champagne/25 blur-3xl pointer-events-none" />

            <div className="relative p-6 sm:p-10 md:p-14">
              {/* Header */}
              <div className="text-center flex flex-col items-center gap-3 sm:gap-4">
                <span className="inline-flex items-center gap-2 text-[10px] sm:text-xs tracking-[0.32em] uppercase text-plum font-semibold">
                  <Sparkles size={14} /> {EVENT.eyebrow || "Special event"}
                </span>
                {EVENT.dates_label && (
                  <div
                    data-testid="event-dates"
                    className="inline-block rounded-full bg-plum text-white text-xs sm:text-sm font-medium px-5 sm:px-6 py-2 shadow-md tracking-wide"
                  >
                    {EVENT.dates_label}
                  </div>
                )}
              </div>

              {/* Main grid */}
              <div className="mt-8 sm:mt-10 grid md:grid-cols-[1.4fr_1fr] gap-8 sm:gap-10 items-start">
                <div>
                  <h2
                    data-testid="event-title"
                    className="font-display text-3xl sm:text-4xl md:text-5xl text-plum-deep leading-[1.05]"
                  >
                    {EVENT.title}
                  </h2>
                  {EVENT.inclusions && EVENT.inclusions.length > 0 && (
                    <div className="mt-5 sm:mt-6">
                      <div className="text-[10px] sm:text-xs uppercase tracking-[0.25em] text-plum font-semibold">
                        Your treatment includes
                      </div>
                      <ul className="mt-3 space-y-2">
                        {EVENT.inclusions.map((inc, i) => (
                          <li
                            key={i}
                            data-testid={`event-inclusion-${i}`}
                            className="flex items-start gap-3 text-sm sm:text-base text-foreground/80 leading-relaxed"
                          >
                            <Sparkles
                              size={14}
                              className="text-plum mt-1 shrink-0"
                            />
                            <span>{inc}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {EVENT.outcome && (
                    <p className="mt-5 sm:mt-6 text-plum-deep font-display italic text-lg sm:text-xl">
                      {EVENT.outcome}
                    </p>
                  )}
                </div>

                <div className="flex flex-col items-center gap-4 sm:gap-5">
                  {EVENT.price_label && (
                    <div
                      data-testid="event-price"
                      className="w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-plum text-white flex flex-col items-center justify-center shadow-lg border-2 border-champagne/60 text-center"
                    >
                      <div className="text-[10px] tracking-[0.3em] uppercase text-champagne">
                        {EVENT.price_label.replace(/£.*$/, "").trim() || "Only"}
                      </div>
                      <div className="font-display text-3xl sm:text-4xl mt-1">
                        {(EVENT.price_label.match(/£[\d.]+/) || ["£15"])[0]}
                      </div>
                    </div>
                  )}
                  {EVENT.badge && (
                    <div className="text-center text-[10px] sm:text-xs uppercase tracking-[0.2em] text-plum font-semibold">
                      {EVENT.badge}
                    </div>
                  )}
                </div>
              </div>

              {/* Upgrade panel */}
              {(EVENT.upgrade_title || EVENT.upgrade_description) && (
                <div
                  data-testid="event-upgrade"
                  className="mt-8 sm:mt-10 relative overflow-hidden rounded-2xl bg-plum text-white p-6 sm:p-8 flex flex-wrap items-center justify-between gap-5"
                >
                  <div className="absolute inset-0 bg-grain opacity-25 mix-blend-overlay pointer-events-none" />
                  <div className="relative min-w-0 flex-1">
                    <div className="text-[10px] sm:text-xs tracking-[0.3em] uppercase text-champagne font-semibold">
                      {EVENT.upgrade_title}
                    </div>
                    <p className="mt-2 font-display text-xl sm:text-2xl leading-snug">
                      {EVENT.upgrade_description}
                    </p>
                  </div>
                  {EVENT.upgrade_saving_label && (
                    <div className="relative shrink-0 rounded-full border border-champagne/60 px-5 py-3 text-center">
                      <div className="text-[10px] tracking-[0.25em] uppercase text-champagne">
                        Save
                      </div>
                      <div className="font-display text-xl sm:text-2xl mt-0.5">
                        {EVENT.upgrade_saving_label}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* CTA + footer */}
              <div className="mt-8 sm:mt-10 text-center">
                {EVENT.tagline && (
                  <p className="text-plum-deep font-display italic text-lg sm:text-xl mb-5">
                    {EVENT.tagline}
                  </p>
                )}
                <Link
                  to={`/contact?special=${encodeURIComponent(
                    EVENT.cta_special_name || EVENT.title || "Skincare event"
                  )}`}
                  data-testid="event-book-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-plum text-white px-7 sm:px-8 py-3.5 text-sm font-medium hover:bg-plum-light transition-all hover:-translate-y-0.5 shadow-lg"
                >
                  {EVENT.cta_label || "Book my spot"} <ArrowRight size={16} />
                </Link>
                {EVENT.footer_note && (
                  <p className="mt-4 text-xs sm:text-sm text-plum font-medium">
                    {EVENT.footer_note}
                  </p>
                )}
                {EVENT.location_line && (
                  <p className="mt-2 text-[11px] sm:text-xs uppercase tracking-[0.2em] text-foreground/60">
                    {EVENT.location_line}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* MONTHLY SPECIALS */}
      {showOffers && (
        <section className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 py-6 sm:py-10" data-testid="offers-section">
          <div className="relative rounded-[1.5rem] sm:rounded-[2rem] overflow-hidden border border-champagne/60 bg-gradient-to-br from-plum to-plum-light text-white">
            <div className="absolute inset-0 bg-grain opacity-30 mix-blend-overlay pointer-events-none" />
            <div className="relative p-6 sm:p-8 md:p-12 grid lg:grid-cols-[1fr_2fr] gap-8 sm:gap-10 items-start">
              <div>
                <span className="inline-flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-champagne font-semibold">
                  <Sparkles size={12} /> {OFFERS.subtitle || "Limited-time offers"}
                </span>
                <h2 className="font-display text-3xl sm:text-4xl md:text-5xl mt-3 leading-[1.05]">
                  {OFFERS.title || "This month's specials"}
                </h2>
                <p className="mt-4 sm:mt-5 text-blush/85 text-sm leading-relaxed max-w-md">
                  Treat yourself this month — these prices are only available until the end of the month, while spaces last.
                </p>
                <Link
                  to="/contact"
                  data-testid="offers-cta"
                  className="mt-6 sm:mt-7 inline-flex items-center gap-2 rounded-full bg-white text-plum px-6 py-3 text-sm font-medium hover:bg-blush transition-all hover:-translate-y-0.5"
                >
                  Claim a special <ArrowRight size={14} />
                </Link>
              </div>
              <div>
                <ul className="space-y-3">
                  {OFFERS.items.map((it, i) => (
                    <li
                      key={i}
                      data-testid={`offer-item-${i}`}
                      className="flex flex-wrap items-baseline justify-between gap-2 sm:gap-4 bg-white/8 backdrop-blur-sm rounded-2xl px-4 sm:px-5 py-3 sm:py-4 border border-white/15"
                    >
                      <span className="font-display text-base sm:text-lg md:text-xl">{it.name}</span>
                      <span className="font-display text-xl sm:text-2xl text-champagne whitespace-nowrap">{it.price}</span>
                    </li>
                  ))}
                </ul>
                {OFFERS.footer_note && (
                  <p className="mt-4 sm:mt-5 text-[10px] sm:text-xs uppercase tracking-[0.2em] text-champagne">
                    {OFFERS.footer_note}
                  </p>
                )}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CATEGORIES */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-12">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
              What we offer
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-plum-deep mt-3">
              Treatments crafted with care
            </h2>
          </div>
          <Link
            to="/treatments"
            data-testid="categories-see-all-link"
            className="text-sm text-plum hover:text-plum-light inline-flex items-center gap-1"
          >
            See full menu <ArrowRight size={14} />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          {categories.map((c) => (
            <Link
              key={c.title}
              to={c.cta}
              data-testid={c.test}
              className="group relative overflow-hidden rounded-3xl border border-blush-deep/40 bg-white"
            >
              <div className="aspect-[4/5] overflow-hidden">
                <img
                  src={c.image}
                  alt={c.title}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              </div>
              <div className="p-6">
                <h3 className="font-display text-2xl text-plum">{c.title}</h3>
                <p className="text-sm text-foreground/65 mt-2 leading-relaxed">{c.desc}</p>
                <span className="inline-flex items-center gap-1 mt-4 text-xs uppercase tracking-[0.2em] text-plum font-semibold">
                  Explore <ArrowRight size={12} />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* EXTRAS: gift vouchers, pamper parties, loyalty cards */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 pb-4" data-testid="extras-section">
        <div className="mb-10">
          <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
            Little extras
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-plum-deep mt-3">
            More ways to treat yourself
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          <div
            data-testid="gift-voucher-banner"
            className="rounded-3xl border border-champagne/60 bg-gradient-to-br from-blush via-secondary to-blush p-8 flex flex-col"
          >
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-plum text-white">
              <Gift size={18} />
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-plum-deep mt-5">
              Gift vouchers
            </h3>
            <p className="mt-2 text-foreground/70 text-sm leading-relaxed flex-1">
              The perfect treat for someone you love — just ask in the enquiry
              section and Deborah will arrange it for you.
            </p>
            <Link
              to="/contact?enquiry=gift-voucher"
              data-testid="gift-voucher-cta"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-plum hover:text-plum-light transition"
            >
              Enquire about a voucher <ArrowRight size={14} />
            </Link>
          </div>

          <div
            data-testid="pamper-party-banner"
            className="rounded-3xl border border-champagne/60 bg-gradient-to-br from-blush via-secondary to-blush p-8 flex flex-col"
          >
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-plum text-white">
              <PartyPopper size={18} />
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-plum-deep mt-5">
              Pamper parties &amp; mobile treatments
            </h3>
            <p className="mt-2 text-foreground/70 text-sm leading-relaxed flex-1">
              Enjoy the full treatment list in the comfort of your own home —
              perfect for birthdays, hen parties, baby showers or a girls' night
              in.
            </p>
            <Link
              to="/contact?enquiry=pamper-party"
              data-testid="pamper-party-cta"
              className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-plum hover:text-plum-light transition"
            >
              Enquire about a party <ArrowRight size={14} />
            </Link>
          </div>

          <div
            data-testid="loyalty-card-banner"
            className="rounded-3xl border border-champagne/60 bg-gradient-to-br from-blush via-secondary to-blush p-8 flex flex-col"
          >
            <div className="inline-flex items-center justify-center w-11 h-11 rounded-full bg-plum text-white">
              <Stamp size={18} />
            </div>
            <h3 className="font-display text-2xl md:text-3xl text-plum-deep mt-5">
              Loyalty cards
            </h3>
            <p className="mt-2 text-foreground/70 text-sm leading-relaxed flex-1">
              Save your stamps with every visit and receive a{" "}
              <strong className="text-plum">free gift</strong> and a{" "}
              <strong className="text-plum">complimentary treatment</strong> — just
              ask Deborah for your card at your next appointment.
            </p>
            <span className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-plum font-semibold">
              <Sparkles size={12} /> Free with every visit
            </span>
          </div>
        </div>
      </section>

      {/* ABOUT TEASER */}
      <section className="bg-secondary/40 py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10 grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="rounded-[2rem] overflow-hidden shadow-xl">
              <img src={IMAGES.deborah} alt="Deborah Louise" loading="lazy" decoding="async" className="w-full object-cover" />
            </div>
          </div>
          <div>
            <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
              Your therapist
            </span>
            <h2 className="font-display text-4xl md:text-5xl text-plum-deep mt-3">
              Meet Deborah Louise
            </h2>
            <p className="mt-6 text-foreground/75 text-lg leading-relaxed">
              With a gentle, intuitive touch and years of experience, Deborah specialises in
              hot stone, Lomi Lomi and pregnancy massage — alongside Dr Hauschka facials and
              precise nail care. Every treatment is shaped around how you arrive that day.
            </p>
            <Link
              to="/about"
              data-testid="home-about-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-full border border-plum/30 text-plum px-6 py-3 text-sm font-medium hover:bg-plum/5 transition"
            >
              Read her story <ArrowRight size={14} />
            </Link>
          </div>
        </div>
      </section>

      {/* REVIEW STRIP */}
      <section className="mx-auto max-w-7xl px-6 lg:px-10 py-20">
        <div className="text-center mb-12">
          <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
            Loved by clients
          </span>
          <h2 className="font-display text-4xl md:text-5xl text-plum-deep mt-3">
            100% five-star reviews
          </h2>
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {REVIEWS.slice(0, 2).map((r, i) => (
            <figure
              key={i}
              className="bg-white rounded-3xl p-8 border border-blush-deep/40 relative"
              data-testid={`home-review-${i}`}
            >
              <Quote className="text-blush-deep" size={28} />
              <blockquote className="mt-4 text-foreground/80 leading-relaxed italic">
                "{r.text}"
              </blockquote>
              <figcaption className="mt-6 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-plum">{r.name}</div>
                  <div className="text-xs text-foreground/55">{r.treatment} · {r.date}</div>
                </div>
                <Stars />
              </figcaption>
            </figure>
          ))}
        </div>
        <div className="text-center mt-10">
          <Link
            to="/reviews"
            data-testid="home-all-reviews-link"
            className="text-sm text-plum hover:text-plum-light inline-flex items-center gap-1"
          >
            Read more reviews <ArrowRight size={14} />
          </Link>
        </div>
      </section>

      {/* LOCATIONS STRIP */}
      <section className="bg-plum text-white py-20">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <div className="grid md:grid-cols-3 gap-10">
            {LOCATIONS.map((l) => (
              <div key={l.id} data-testid={`home-location-${l.id}`}>
                <div className="text-xs tracking-[0.3em] uppercase text-champagne mb-3">
                  {l.day}
                </div>
                <div className="font-display text-2xl">{l.name}</div>
                <div className="text-sm text-blush/80 mt-2">{l.address}</div>
                <div className="text-sm text-white/90 mt-1">{l.hours}</div>
              </div>
            ))}
          </div>
          <div className="mt-12 flex flex-wrap items-center gap-4">
            <Link
              to="/contact"
              data-testid="locations-strip-book-cta"
              className="inline-flex items-center gap-2 rounded-full bg-white text-plum px-7 py-3.5 text-sm font-medium hover:bg-blush transition"
            >
              Reserve your slot <ArrowRight size={16} />
            </Link>
            <a
              href={`tel:${BRAND.phoneTel}`}
              data-testid="locations-strip-call-link"
              className="text-sm text-blush/90 hover:text-white"
            >
              or call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
